import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Eye, EyeOff, Loader2, LogIn, Mail, Lock, AlertCircle } from "lucide-react";
import { usePortalAuth } from "../../context/PortalAuthContext";
import PortalShell from "./PortalShell";
import SEO from "../../components/SEO";
import { TotpStep } from "../admin/Login";
import { formatApiError } from "../../lib/api";

export default function PortalLogin() {
  const { customer, loading, login, verifyTotp } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [challenge, setChallenge] = useState(null);
  const [code, setCode] = useState("");
  const codeRef = useRef(null);

  useEffect(() => { if (challenge && codeRef.current) codeRef.current.focus(); }, [challenge]);

  if (loading) {
    return (
      <PortalShell showNav={false}>
        <div className="min-h-[50vh] flex items-center justify-center">
          <Loader2 className="animate-spin text-zinc-500" />
        </div>
      </PortalShell>
    );
  }
  if (customer) {
    const dest = location.state?.from || "/portal";
    return <Navigate to={dest} replace />;
  }

  const submitPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const data = await login(email.trim().toLowerCase(), password);
      setChallenge(data);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Couldn't sign you in right now. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await verifyTotp(challenge.challenge_token, code);
      navigate(location.state?.from || "/portal", { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Invalid code.");
    } finally {
      setSubmitting(false);
    }
  };

  const restart = () => { setChallenge(null); setCode(""); setError(""); };

  return (
    <PortalShell showNav={false}>
      <SEO
        title="Customer Portal · Tech Emergency"
        description="Sign in to your Tech Emergency customer portal."
        path="/portal/login"
        noIndex
      />
      <div className="max-w-md mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="te-overline mb-3">/ Customer portal</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tighter leading-tight mb-3">
          Welcome back.
        </h1>
        <p className="text-zinc-400 text-sm leading-relaxed mb-8">
          Sign in to view your past jobs, service notes and costs.
        </p>

        {!challenge && (
          <form onSubmit={submitPassword} className="space-y-4" data-testid="portal-login-form">
            <label className="block">
              <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
                <Mail size={11} /> Email
              </span>
              <input
                type="email" required autoFocus value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="portal-login-email" autoComplete="email"
                className="w-full bg-black/40 border border-white/10 px-3 py-2.5 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
              />
            </label>
            <label className="block">
              <span className="te-overline text-[10px] block mb-1.5 flex items-center gap-1.5">
                <Lock size={11} /> Password
              </span>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="portal-login-password" autoComplete="current-password"
                  className="w-full bg-black/40 border border-white/10 px-3 py-2.5 pr-10 text-sm focus:outline-none focus:border-[var(--te-primary)] transition-colors"
                />
                <button type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? "Hide password" : "Show password"}
                  data-testid="portal-login-toggle-pw"
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-zinc-400 hover:text-white transition">
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </label>
            {error && (
              <div className="text-sm px-3 py-2 border border-red-500/30 bg-red-500/10 text-red-300 flex items-start gap-2" data-testid="portal-login-error">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            <button type="submit" disabled={submitting} data-testid="portal-login-submit"
              className="te-btn te-btn-primary w-full justify-center disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? (<><Loader2 size={14} className="animate-spin" /> Continuing…</>) : (<><LogIn size={14} /> Continue</>)}
            </button>
          </form>
        )}

        {challenge && (
          <TotpStep
            challenge={challenge}
            issuer="Tech Emergency · Portal"
            email={email}
            code={code}
            setCode={setCode}
            codeRef={codeRef}
            submit={submitCode}
            submitting={submitting}
            restart={restart}
            error={error}
            testidPrefix="portal-2fa"
          />
        )}

        <div className="mt-8 text-xs text-zinc-500 leading-relaxed">
          Don&apos;t have a portal account yet? We&apos;ll set you up after your
          next job &mdash; or call us on{" "}
          <a href="tel:1800836390" className="text-white hover:text-[var(--te-primary)] transition">
            1800 836 390
          </a>{" "}
          and we&apos;ll email you an invite right now.
        </div>
        <div className="mt-4 text-xs text-zinc-600">
          <Link to="/" className="hover:text-zinc-300 transition">
            ← Back to main site
          </Link>
        </div>
      </div>
    </PortalShell>
  );
}
